import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOGOUT_MINUTES = {
  super_admin: 15,
  admin: 15,
  national: 20,
  subscriber: 20,
};

const COMMUNICATIONS_IDLE_MINUTES = 15;
const COMMUNICATIONS_LAST_ACTIVITY = "communications_last_activity_at";

const PROTECTED_PREFIXES = [
  "/portal",
  "/nationals",
  "/subscribers",
  "/admin",
  "/members",
  "/communications",
  "/yasharal-live",
  "/account/settings",
];

function getCookie(name) {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? decodeURIComponent(match[2]) : null;
}

async function getCsrfToken() {
  const cookieToken = getCookie("ysq_csrf");
  if (cookieToken) return cookieToken;

  const storedToken = localStorage.getItem("ysq_csrf");
  if (storedToken) return storedToken;

  try {
    const response = await axios.get(`${API}/auth/csrf`, { withCredentials: true });
    const csrfToken = response.data?.csrf_token;
    if (csrfToken) localStorage.setItem("ysq_csrf", csrfToken);
    return csrfToken;
  } catch {
    return null;
  }
}

function getLogoutMinutes(user, communicationsRoute) {
  if (communicationsRoute) return COMMUNICATIONS_IDLE_MINUTES;

  const roles = user?.roles || [];
  const adminScopes = user?.admin_scopes || [];

  if (roles.includes("admin") && adminScopes.includes("*")) return LOGOUT_MINUTES.super_admin;
  if (roles.includes("admin")) return LOGOUT_MINUTES.admin;
  if (roles.includes("national")) return LOGOUT_MINUTES.national;
  if (roles.includes("subscriber")) return LOGOUT_MINUTES.subscriber;
  return null;
}

export default function AutoLogout() {
  const timerRef = useRef(null);
  const location = useLocation();
  const [user, setUser] = useState(null);

  const liveServiceRoute = location.pathname.startsWith("/yasharal-live");
  const communicationsRoute = location.pathname.startsWith("/communications") || liveServiceRoute;
  const publicAuthRoute = ["/yasharal-live/login", "/yasharal-live/signup"].includes(location.pathname);
  const isProtectedRoute = !publicAuthRoute && PROTECTED_PREFIXES.some((prefix) =>
    location.pathname.startsWith(prefix)
  );

  useEffect(() => {
    let cancelled = false;

    const fetchUser = async () => {
      if (!isProtectedRoute) {
        setUser(null);
        return;
      }

      try {
        const response = await axios.get(`${API}/nationals/me`, { withCredentials: true });
        if (!cancelled) setUser(response.data);
      } catch {
        if (!cancelled) setUser(null);
      }
    };

    fetchUser();
    return () => { cancelled = true; };
  }, [isProtectedRoute, location.pathname]);

  useEffect(() => {
    if (!isProtectedRoute || !user) {
      if (timerRef.current) clearTimeout(timerRef.current);
      return undefined;
    }

    const logoutMinutes = getLogoutMinutes(user, communicationsRoute);
    if (!logoutMinutes) return undefined;
    const logoutMs = logoutMinutes * 60 * 1000;

    let logoutInFlight = false;
    const redirectToSignIn = () => {
      const isLiveHost = window.location.hostname === "live.yasharal.vip";
      window.location.replace(isLiveHost ? "/yasharal-live/login" : "/nationals/login");
    };
    const logout = async () => {
      if (logoutInFlight) return;
      logoutInFlight = true;
      // The output worker continues independently of browser activity. Renew
      // this user's cookie ONLY if the backend confirms their own live output.
      // A stopped stream or another user's output never extends the session.
      try {
        const active = await axios.get(`${API}/communications/auth/active-output`);
        if (active.data?.active) {
          const renewed = await axios.post(`${API}/communications/auth/stream-keepalive`);
          if (renewed.data?.active) {
            logoutInFlight = false;
            scheduleFrom(Date.now());
            return;
          }
        }
      } catch (error) {
        // If credentials have expired or are revoked, do not renew or render
        // private controls. Offline failures also cannot establish an exemption.
        if (error?.response?.status === 401 || error?.response?.status === 403) {
          localStorage.removeItem("ysq_csrf");
          if (communicationsRoute) sessionStorage.removeItem(COMMUNICATIONS_LAST_ACTIVITY);
          redirectToSignIn();
          return;
        }
        // A temporary network outage cannot verify whether a producer is
        // broadcasting. Retry soon; do not unnecessarily sign out a running
        // unattended production because the status check briefly failed.
        logoutInFlight = false;
        scheduleFrom(Date.now() - logoutMs + 60_000);
        return;
      }
      try {
        const csrf = await getCsrfToken();
        await axios.post(
          `${API}/auth/logout`,
          {},
          { withCredentials: true, headers: { ...(csrf ? { "X-CSRF-Token": csrf } : {}) } }
        );
      } catch {
        // Fall back to the signed-out page even when the API is unreachable.
      } finally {
        localStorage.removeItem("ysq_csrf");
        if (communicationsRoute) sessionStorage.removeItem(COMMUNICATIONS_LAST_ACTIVITY);
        toast.info(`You were logged out after ${logoutMinutes} minutes of inactivity.`);
        redirectToSignIn();
      }
    };

    const scheduleFrom = (timestamp) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      const elapsed = Date.now() - timestamp;
      if (elapsed >= logoutMs) {
        logout();
        return;
      }
      timerRef.current = setTimeout(logout, logoutMs - elapsed);
    };

    const resetTimer = () => {
      const now = Date.now();
      if (communicationsRoute) sessionStorage.setItem(COMMUNICATIONS_LAST_ACTIVITY, String(now));
      scheduleFrom(now);
    };

    if (communicationsRoute) {
      const prior = Number(sessionStorage.getItem(COMMUNICATIONS_LAST_ACTIVITY) || 0);
      if (prior > 0) scheduleFrom(prior);
      else resetTimer();
    } else {
      resetTimer();
    }

    const activityEvents = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"];
    activityEvents.forEach((eventName) => window.addEventListener(eventName, resetTimer));

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      activityEvents.forEach((eventName) => window.removeEventListener(eventName, resetTimer));
    };
  }, [communicationsRoute, isProtectedRoute, user]);

  return null;
}
